"use client"

import type { Omit } from "@wagmi/core/internal"
import { Evaluate, zeroAddress } from "viem"
import { useChainId, useConfig } from "wagmi"

import { isAddress } from "@eulerxyz/core-sdk"
import { useMemo } from "react"
import type { GetAccountInfoErrorType } from "../actions/getAccountInfo"
import {
  type GetAccountInfoData,
  type GetAccountInfoOptions,
  type GetAccountInfoQueryFnData,
  type GetAccountInfoQueryKey,
  getAccountInfoQueryOptions,
} from "../query"
import type {
  ConfigParameter,
  ExtendedConfig,
  UseQueryParameters,
  UseQueryReturnType,
} from "../types"
import { useQuery } from "../utils"

export type UseAccountInfoParameters<
  config extends ExtendedConfig = ExtendedConfig,
  selectData = GetAccountInfoData,
> = Evaluate<
  GetAccountInfoOptions<config> &
    ConfigParameter<config> & {
      query?:
        | Evaluate<
            Omit<
              UseQueryParameters<
                GetAccountInfoQueryFnData,
                GetAccountInfoErrorType,
                selectData,
                GetAccountInfoQueryKey<config>
              >,
              "gcTime" | "staleTime"
            >
          >
        | undefined
    }
>

export type UseAccountInfoReturnType<selectData = GetAccountInfoData> =
  UseQueryReturnType<selectData, GetAccountInfoErrorType>

export function useAccountInfo<
  config extends ExtendedConfig,
  selectData = GetAccountInfoData,
>(
  parameters: UseAccountInfoParameters<config, selectData> = {},
): UseAccountInfoReturnType<selectData> {
  const {
    query = {},
    vault,
    evcAddress,
    accountLensAddress,
    account,
  } = parameters

  const config = useConfig(parameters)
  const chainId = useChainId({ config })

  const { queryKey, ...options } = getAccountInfoQueryOptions(config, {
    account,
    vault,
    chainId: parameters.chainId ?? chainId,
    evcAddress,
    accountLensAddress,
  })

  const enabled = useMemo(
    () =>
      Boolean(
        isAddress(vault) &&
          vault !== zeroAddress &&
          isAddress(account) &&
          account !== zeroAddress &&
          (query.enabled ?? true),
      ),
    [vault, account, query.enabled],
  )

  const result = useQuery({
    ...query,
    ...options,
    queryKey,
    enabled,
    refetchOnMount: false,
    retry: false,
  })

  if (result.isError) {
    console.error("[Error][useAccountInfo] result", result.error)
  }

  return result
}
